package ui.location.detail

import androidx.compose.runtime.*
import androidx.compose.ui.graphics.vector.*
import api.*
import common.frontend.api.*
import org.jetbrains.compose.resources.*
import provider.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.common.msg.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.compose.navigation.viewmodel.*
import tools.*
import ui.*
import ui.widget.*
import kotlin.time.*
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds


interface LocationScope {

    val vm: LocationVM
    val vmGlobal: GlobalVM
    val action: (LocationVM.Action) -> Unit

    val args: LocationArgs
    val nav: Navigation
    val login: Io.User
    val display: CommonDisplayProvider
    val groupActions: List<DeviceAction>

    data class TimerValue(
        val value: Duration,
        override val label: String = value.toString(),
        override val image: ImageVector? = null
    ) : DropDownItem

    data class LayoutType(
        val type: KioskLayoutType,
        override val label: StringResource = when (type) {
            KioskLayoutType.NO_BRANDING -> Res.string.location_detail__branding_dashboard_layout_no_branding
            KioskLayoutType.STANDARD -> Res.string.location_detail__branding_dashboard_layout_standard
        },
        override val image: ImageVector? = null
    ) : DropDownResItem

    class DeviceAction(
        val name: StringResource,
        val act: Message.Task.Keys
    ) {
        companion object {
            val SPACE = DeviceAction(Res.string.global_empty, Message.Task.Keys.UNKNOWN)
            fun build(login: Io.User, targetCompanyId: Long) = buildList {
                if (login.canDeviceActionsView(targetCompanyId)) {
                    add(DeviceAction(Res.string.device_detail__actions__update, Message.Task.Keys.UPDATE))
                    add(SPACE)
                    if (login.canDeviceEdit(targetCompanyId)) {
                        add(DeviceAction(Res.string.device_detail__actions__resend_settings, Message.Task.Keys.CONFIGURATION))
                    }
                    if (login.canLayerTopView(targetCompanyId)) {
                        add(DeviceAction(Res.string.device_detail__actions__resend_playlist, Message.Task.Keys.PLAYLIST))
                    }
                    if (login.canLayerBottomView(targetCompanyId)) {
                        add(DeviceAction(Res.string.device_detail__actions__resend_apps, Message.Task.Keys.APPS))
                    }
                    add(SPACE)
                    add(DeviceAction(Res.string.device_detail__actions__restart, Message.Task.Keys.RESTART_APP))
                    add(DeviceAction(Res.string.device_detail__actions__reboot, Message.Task.Keys.REBOOT_DEVICE))
                }
            }
        }
    }

    companion object {
        val timerVariants by lazy {
            listOf(
                TimerValue(10.seconds),
                TimerValue(20.seconds),
                TimerValue(30.seconds),
                TimerValue(1.minutes),
                TimerValue(2.minutes),
                TimerValue(5.minutes),
                TimerValue(10.minutes),
                TimerValue(15.minutes),
                TimerValue(20.minutes),
            )
        }

        val layoutVariants by lazy {
            KioskLayoutType.all.map {
                LayoutType(it)
            }
        }

        @Composable
        fun rememberScope(): LocationScope {
            val nav = LocalNavigation.current
            val account = LocalAccountProvider.current
            val args = LocalNavigationArgs.current.rememberArgs(LocationArgs::class)
            val vm = nav.viewModel(LocationVM::class, "${args.companyId}:${args.locationId}")
            val vmGlobal = globalViewModel(GlobalVM::class)
            val action = LocationVM.Action.rememberAction(vm)
            val display = LocalDisplayProvider.current
            return remember {
                val groupActions = DeviceAction.build(account.current!!, args.companyId)
                object : LocationScope {
                    override val vm: LocationVM
                        get() = vm
                    override val vmGlobal: GlobalVM
                        get() = vmGlobal
                    override val action: (LocationVM.Action) -> Unit
                        get() = action
                    override val args: LocationArgs
                        get() = args
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val display: CommonDisplayProvider
                        get() = display
                    override val groupActions: List<DeviceAction>
                        get() = groupActions


                }.also {
                    vm.init(it)
                }
            }
        }
    }


    class Wrapper(
        val canEdit: Boolean,
        val canDelete: Boolean,
        val canEmergencyVisualAdd: Boolean,
        val canEmergencyVisualActivate: Boolean,
        override val data: Io.Location,
        override val key: String = data.id.toString(),
        val branding: WrapperBranding = WrapperBranding(canEdit) { data }
    ) : EFieldList.Entry<Io.Location> {


        val emergency: WrapperEmergencyImages = WrapperEmergencyImages({ data }) {
            makeChange()
        }

        private var origin = data.copy(
            days = data.days.map { it.copy() }.toMutableList(),
            emergency = data.emergency.copy(
                variants = data.emergency.variants.map { it.copy() }.toMutableList()
            )
        )

        val name = EFieldTextString(
            property = data::name,
            condition = { it.trim().takeIf { it.length > 3 } },
            onChange = { makeChange() },
            onErr = { makeChange() })

        val days = WrapperDays(data.days) {
            makeChange()
        }

        var change by mutableStateOf(false)
            private set

        fun updateOrigin() {
            origin = data.copy(
                days = data.days.map { it.copy() }.toMutableList(),
                emergency = data.emergency.copy(
                    variants = data.emergency.variants.map { it.copy() }.toMutableList()
                )
            )
            branding.updateOrigin()
            makeChange()
        }

        private fun makeChange() {
            change = if (name.err) false
            else if (origin.name != data.name) true
            else if (origin.emergency.variants.size != data.emergency.variants.size) true
            else if (origin.emergency.variants.indices.any { data.emergency.variants[it].id != data.emergency.variants[it].id }) true
            else if (daysAreChanged()) true
            else false
        }

        private fun daysAreChanged(): Boolean {
            for (index in origin.days.indices) {
                if (origin.days[index] != data.days[index]) return true
            }
            return false
        }
    }


    class WrapperBranding(
        val canEdit: Boolean,
        val data: () -> Io.Location,
    ) {

        val images = WrapperBrandingImages(data().branding) {
            makeChange()
        }

        var change by mutableStateOf(false)
            private set

        private var origin = data().branding.copy(
            images = data().branding.images.sorted().toMutableList()
        )

        fun updateOrigin() {
            origin = data().branding.copy(
                images = data().branding.images.sorted().toMutableList()
            )
            change = false
        }

        val name = EFieldTextString(
            property = data().branding::name,
            condition = { it.trim() },
            onChange = { makeChange() },
            onErr = { makeChange() })

        val closeWarningTitle = EFieldTextString(
            property = data().branding.text::closeWarningTitle,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = { makeChange() },
            onErr = { makeChange() })

        val closeWarningDesc = EFieldTextString(
            property = data().branding.text::closeWarningDesc,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = { makeChange() },
            onErr = { makeChange() })

        val closeWarningAction = EFieldTextString(
            property = data().branding.text::closeWarningAction,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = { makeChange() },
            onErr = { makeChange() })

        val logo = EFieldRegularLong(
            property = data().branding::logo,
            onChange = { makeChange() })

        val dashboardIconScale = EFieldRegularInt(
            property = data().branding.sizes::dashboardIconScale,
            onChange = { makeChange() })

        val dashboardSlideshowCrop = EFieldRegularBoolean(
            property = data().branding.params::slideshowCropImages,
            onChange = { makeChange() })

        val dashboardLayout = EFieldRegularInt(
            property = data().branding.layout::type,
            onChange = { makeChange() })

        val overlayLand = EFieldRegularLong(
            property = data().branding.overlay::land,
            onChange = { makeChange() })

        val overlayPort = EFieldRegularLong(
            property = data().branding.overlay::port,
            onChange = { makeChange() })

        val timeoutHandicap = EFieldRegularLong(
            property = data().branding.timeout::handicap,
            onChange = { makeChange() })

        val timeoutScreen = EFieldRegularLong(
            property = data().branding.timeout::screen,
            onChange = { makeChange() })

        val timeoutDialog = EFieldRegularLong(
            property = data().branding.timeout::dialog,
            onChange = { makeChange() })

        var background = EFieldColorAsLong(
            property = data().branding.colors::background,
            onChange = { makeChange() },
            onErr = { makeChange() })
        var onBackground = EFieldColorAsLong(
            property = data().branding.colors::onBackground,
            onChange = { makeChange() },
            onErr = { makeChange() })

        var primary = EFieldColorAsLong(
            property = data().branding.colors::primary,
            onChange = { makeChange() },
            onErr = { makeChange() })
        var onPrimary = EFieldColorAsLong(
            property = data().branding.colors::onPrimary,
            onChange = { makeChange() },
            onErr = { makeChange() })

        var secondary = EFieldColorAsLong(
            property = data().branding.colors::secondary,
            onChange = { makeChange() },
            onErr = { makeChange() })
        var onSecondary = EFieldColorAsLong(
            property = data().branding.colors::onSecondary,
            onChange = { makeChange() },
            onErr = { makeChange() })

        var primaryContainer = EFieldColorAsLong(
            property = data().branding.colors::primaryContainer,
            onChange = { makeChange() },
            onErr = { makeChange() })
        var onPrimaryContainer = EFieldColorAsLong(
            property = data().branding.colors::onPrimaryContainer,
            onChange = { makeChange() },
            onErr = { makeChange() })

        var secondaryContainer = EFieldColorAsLong(
            property = data().branding.colors::secondaryContainer,
            onChange = { makeChange() },
            onErr = { makeChange() })

        var onSecondaryContainer = EFieldColorAsLong(
            property = data().branding.colors::onSecondaryContainer,
            onChange = { makeChange() },
            onErr = { makeChange() })

        private fun makeChange() {
            val newImages = images.state.map { it.data }.sorted()
            change = if (name.err) false
            /**/
            else if (background.err) false
            else if (primary.err) false
            else if (secondary.err) false
            else if (primaryContainer.err) false
            else if (secondaryContainer.err) false
            else if (onBackground.err) false
            else if (onPrimary.err) false
            else if (onSecondary.err) false
            else if (onPrimaryContainer.err) false
            else if (onSecondaryContainer.err) false
            /**/
            else if (closeWarningTitle.err) false
            else if (closeWarningDesc.err) false
            else if (closeWarningAction.err) false
            /**/
            else if (origin.logo != logo.state) true
            else if (origin.overlay.land != overlayLand.state) true
            else if (origin.overlay.port != overlayPort.state) true
            /**/
            else if (origin.timeout.handicap != timeoutHandicap.state) true
            else if (origin.timeout.screen != timeoutScreen.state) true
            else if (origin.timeout.dialog != timeoutDialog.state) true
            /**/
            else if (origin.text.closeWarningTitle != closeWarningTitle.state) true
            else if (origin.text.closeWarningDesc != closeWarningDesc.state) true
            else if (origin.text.closeWarningAction != closeWarningAction.state) true
            /**/
            else if (origin.colors.background != background.state.value.toLong()) true
            else if (origin.colors.primary != primary.state.value.toLong()) true
            else if (origin.colors.secondary != secondary.state.value.toLong()) true
            else if (origin.colors.primaryContainer != primaryContainer.state.value.toLong()) true
            else if (origin.colors.secondaryContainer != secondaryContainer.state.value.toLong()) true
            else if (origin.colors.onBackground != onBackground.state.value.toLong()) true
            else if (origin.colors.onPrimary != onPrimary.state.value.toLong()) true
            else if (origin.colors.onSecondary != onSecondary.state.value.toLong()) true
            else if (origin.colors.onPrimaryContainer != onPrimaryContainer.state.value.toLong()) true
            else if (origin.colors.onSecondaryContainer != onSecondaryContainer.state.value.toLong()) true
            /**/
            else if (origin.sizes.dashboardIconScale != dashboardIconScale.state) true
            else if (origin.params.slideshowCropImages != dashboardSlideshowCrop.state) true
            /**/
            else if (newImages.size != origin.images.size) true
            else if (!origin.images.all { it in newImages }) true
            else dashboardLayout.change
        }


    }

    class WrapperBrandingImages(branding: Io.Branding, onChange: () -> Unit) : EFieldList<Long, WrapperBrandingImages.Item>(
        items = branding.images,
        onBindEntry = { Item(it) },
        onChange = { onChange() },
    ) {
        class Item(
            override val data: Long,
            override val key: String = data.toString(),
        ) : Entry<Long>
    }

    class WrapperEmergencyImages(
        private val location: () -> Io.Location,
        onChange: () -> Unit
    ) : EFieldList<Io.Location.Emergency.Entry, WrapperEmergencyImages.Item>(
        items = location().emergency.variants,
        onBindEntry = { Item(it) },
        onChange = { onChange() },
    ) {
        var active by mutableStateOf(location().emergency.active)
            private set
        var activeResource by mutableStateOf(location().emergency.selected)
            private set

        fun activate(id: Long) {
            location().emergency.selected = id
            location().emergency.active = true
            activeResource = id
            active = true
        }

        fun disable() {
            location().emergency.active = false
            active = false
        }

        class Item(
            override val data: Io.Location.Emergency.Entry,
            override val key: String = data.id.toString(),
        ) : Entry<Io.Location.Emergency.Entry>
    }

    class WrapperDays(items: MutableList<Io.ImplLocation.Day>, onChange: () -> Unit) : EFieldList<Io.ImplLocation.Day, WrapperDays.Item>(
        items = items,
        onBindEntry = { Item(it, onChange = onChange) }
    ) {

        var clipboard by mutableStateOf<Item?>(null)

        class Item(
            override val data: Io.ImplLocation.Day,
            override val key: String = data.day.toString(),
            val name: StringResource = DayOfWeek[data.day].toStringResShort(),
            val onChange: () -> Unit
        ) : Entry<Io.ImplLocation.Day> {

            val start: EFieldRegularTime = EFieldRegularTime(
                property = data::start,
                onChange = { onChange() })

            val end: EFieldRegularTime = EFieldRegularTime(
                property = data::end,
                onChange = { onChange() })

            val enabled: EFieldDualBooleanAsInt = EFieldDualBooleanAsInt(
                property = data::enabled,
                onChange = { onChange() })


        }
    }
}
